import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label, Select } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { MapPin, Send, CheckCircle2 } from 'lucide-react';

interface BatchSite { site_id: string; site_reference: string; site_name: string; included: boolean; allocation?: any }
interface Batch { id: string; client_id: string; client_name: string; template_name: string; status: string; due_date?: string; sites: BatchSite[] }
interface Contact { id?: string; _id?: string; site_id: string; name?: string; surname?: string; email?: string; number?: string }

type SiteChoice = { mode: 'site_contact' | 'self'; contact_id?: string; recipient_name?: string; recipient_email?: string; recipient_phone?: string };

export const ClientAllocate: React.FC = () => {
  const [batches, setBatches] = useState<Batch[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [choices, setChoices] = useState<Record<string, Record<string, SiteChoice>>>({});
  const [busy, setBusy] = useState('');

  const load = async () => {
    const r = await api.get<Batch[]>('/survey-batches').catch(() => ({ data: [] as Batch[] }));
    setBatches(r.data);
    const cid = r.data.find(b => b.client_id)?.client_id;
    if (cid) {
      const c = await api.get<Contact[]>(`/clients/${cid}/site-contacts`).catch(() => ({ data: [] as Contact[] }));
      setContacts(c.data);
    }
  };
  useEffect(() => { load(); }, []);

  const setChoice = (bid: string, sid: string, patch: Partial<SiteChoice>) => setChoices(prev => {
    const base: SiteChoice = prev[bid]?.[sid] || { mode: 'site_contact' };
    return { ...prev, [bid]: { ...(prev[bid] || {}), [sid]: { ...base, ...patch } } };
  });

  const submit = async (b: Batch) => {
    const included = b.sites.filter(s => s.included && !s.allocation);
    const allocations = included.map(s => {
      const ch = choices[b.id]?.[s.site_id] || { mode: 'site_contact' as const };
      return { site_id: s.site_id, ...ch };
    });
    // validate
    for (const a of allocations) {
      if (a.mode === 'site_contact' && !a.contact_id && !(a.recipient_email || a.recipient_phone)) {
        toast.error('Each site needs a contact (pick one or enter an email/phone), or set it to self-assess.');
        return;
      }
    }
    setBusy(b.id);
    try {
      await api.post(`/survey-batches/${b.id}/allocate`, { allocations });
      toast.success('Surveys allocated and sent.');
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Could not allocate.');
    } finally { setBusy(''); }
  };

  const pending = batches.filter(b => b.status === 'pending_client_allocation');
  const done = batches.filter(b => b.status === 'completed');

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h2 className="font-headline text-3xl text-raptor-navy tracking-wide">Your surveys</h2>
        <p className="text-sm text-raptor-navy/60 font-body">For each site, choose who completes the survey — a site contact (they get a link) or complete it yourself.</p>
      </div>

      {pending.length === 0 && (
        <Card><CardContent className="py-8 text-center text-sm text-raptor-navy/50 font-body" data-testid="client-allocate-empty">No surveys awaiting allocation.</CardContent></Card>
      )}

      {pending.map(b => (
        <Card key={b.id} data-testid={`client-batch-${b.id}`}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div><CardTitle>{b.template_name}</CardTitle><CardDescription>{b.client_name}{b.due_date ? ` · due ${b.due_date}` : ''}</CardDescription></div>
              <Badge variant="blue">Awaiting allocation</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {b.sites.filter(s => s.included).map(s => {
              const done = !!s.allocation;
              const ch = choices[b.id]?.[s.site_id] || { mode: 'site_contact' as const };
              const siteContacts = contacts.filter(c => c.site_id === s.site_id);
              return (
                <div key={s.site_id} className="border border-border/70 rounded-md p-3 space-y-2" data-testid={`client-site-${s.site_id}`}>
                  <div className="flex items-center gap-2">
                    <MapPin size={14} className="text-raptor-blue" />
                    <span className="text-sm font-semibold text-raptor-navy">{s.site_name || s.site_reference}</span>
                    {s.site_reference && <span className="text-[11px] text-raptor-navy/50 font-tabular">{s.site_reference}</span>}
                    {done && <Badge variant="success" className="ml-auto">Sent</Badge>}
                  </div>
                  {!done && (
                    <>
                      <div className="flex gap-2">
                        <Button type="button" size="sm" variant={ch.mode === 'site_contact' ? 'gradient' : 'outline'}
                          onClick={() => setChoice(b.id, s.site_id, { mode: 'site_contact' })} data-testid={`client-mode-contact-${s.site_id}`}>Site contact</Button>
                        <Button type="button" size="sm" variant={ch.mode === 'self' ? 'gradient' : 'outline'}
                          onClick={() => setChoice(b.id, s.site_id, { mode: 'self' })} data-testid={`client-mode-self-${s.site_id}`}>Self-assess</Button>
                      </div>
                      {ch.mode === 'site_contact' && (
                        <div className="space-y-2">
                          {siteContacts.length > 0 && (
                            <div className="space-y-1">
                              <Label>Choose a contact</Label>
                              <Select value={ch.contact_id || ''} onChange={(e) => setChoice(b.id, s.site_id, { contact_id: e.target.value })} data-testid={`client-contact-select-${s.site_id}`}>
                                <option value="">Enter manually…</option>
                                {siteContacts.map(c => <option key={c.id || c._id} value={c.id || c._id}>{`${c.name || ''} ${c.surname || ''}`.trim()} — {c.email || c.number}</option>)}
                              </Select>
                            </div>
                          )}
                          {!ch.contact_id && (
                            <div className="grid md:grid-cols-3 gap-2">
                              <Input placeholder="Name" value={ch.recipient_name || ''} onChange={(e) => setChoice(b.id, s.site_id, { recipient_name: e.target.value })} data-testid={`client-name-${s.site_id}`} />
                              <Input placeholder="Email" value={ch.recipient_email || ''} onChange={(e) => setChoice(b.id, s.site_id, { recipient_email: e.target.value })} data-testid={`client-email-${s.site_id}`} />
                              <Input placeholder="Mobile" value={ch.recipient_phone || ''} onChange={(e) => setChoice(b.id, s.site_id, { recipient_phone: e.target.value })} data-testid={`client-phone-${s.site_id}`} />
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
            <div className="flex justify-end">
              <Button variant="gradient" disabled={busy === b.id} onClick={() => submit(b)} data-testid={`client-allocate-${b.id}`}>
                <Send size={14} /> {busy === b.id ? 'Sending…' : 'Allocate & send'}
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}

      {done.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><CheckCircle2 size={18} className="text-emerald-600" /> Completed</CardTitle></CardHeader>
          <CardContent className="divide-y divide-border/50">
            {done.map(b => (
              <div key={b.id} className="py-2 flex items-center justify-between">
                <div className="text-sm text-raptor-navy">{b.template_name}</div>
                <Badge variant="success">Allocated</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
